import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { useLocation, useHistory } from 'react-router-dom'
import SVG from 'react-inlinesvg';
import { toAbsoluteUrl } from '../../../_metronic/helpers';

const GuidePage: FC = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const history = useHistory();

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: false },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        })
        return () => setLayout(restoreRef.current)
    }, [])

    return (
        <main className="nobilis">
            <div className="nobilis__container">
                <div>
                    <div className="nb-link-underline-500 w-auto cursor-pointer" onClick={()=> history.goBack()}>
                        Back
                    </div>
                </div>
                <h1 className="nobilis__title">
                    Nobilis Experience Guidelines
                </h1>
                {/* PURPOSE */}
                <section className="nobilis__section">
                    <div className='guide-mark-water'>
                        <SVG src={toAbsoluteUrl('/media/svg/nobilis/mark_bg.svg')} />
                    </div>
                    <h2 className="nobilis__label">
                        Purpose of Nobilis Experiences
                    </h2>

                    <p className="nobilis__text">
                        Nobilis Experiences are the core of the ecosystem.<br />
                        They are not traditional events, but curated invitations into knowledge, culture, access, and presence.
                        They exist to foster meaningful human connection, trusted exchange, and long-term relationship building.
                        <br /><br />
                        Each experience should:<br />
                        <ul>
                            <li>Be aligned with the interests of Nobilis members, be led by the hosting member, and contribute to the long-term integrity and value of the Nobilis community.<br /></li>
                            <li>Respect privacy, discretion, and mutual trust<br /></li>
                        </ul>
                        Nobilis experiences are designed for more intimate gatherings of up to 25 participants. However, larger-scale initiatives may also be hosted where appropriate.<br />
                        Experiences may be private or shared within the Nobilis community, and may be hosted for non-profit, impact-driven, or commercial purposes.<br />
                        All experiences must remain aligned with Nobilis standards, values, and community principles.
                    </p>
                </section>
                {/* WHAT WE STAND FOR */}
                <section className="nobilis__section">
                    <h2 className="nobilis__label">
                        what we stand for
                    </h2>

                    <p className="nobilis__text">
                        Nobilis Experiences reflect the real lives, passions, professions, and values of our members.<br />
                        They may represent lifestyle, hobbies, professional paths, impact initiatives, or access to places, people, and environments that are not publicly accessible.<br />
                        What is ordinary or routine for a member may be extraordinary and meaningful for the community.
                    </p>

                    <div className='gallery-section'>
                        <div className="gallery">
                            <img className="gallery__main-image" src={toAbsoluteUrl('/media/experiences/img-1.jpg')} alt="main" />
                            <div className="gallery__item">
                                <img className="gallery__img" src={toAbsoluteUrl('/media/experiences/img-2.jpg')} alt="man 2" />
                            </div>
                            <div className="gallery__item">
                                <img className="gallery__img" src={toAbsoluteUrl('/media/experiences/img-3.jpg')} alt="man 1" />
                            </div>
                            <div className="gallery__item">
                                <img className="gallery__img" src={toAbsoluteUrl('/media/experiences/img-4.jpg')} alt="woman 1" />
                            </div>
                        </div>
                    </div>

                    <div className="nobilis__subheader mt-4">
                        <h3 className="nobilis__subtitle">
                            Nobilis Experiences
                        </h3>
                        <span className="nobilis__small-label">
                            Experiences may include (but are not limited to):
                        </span>
                    </div>

                    <div className="nobilis__block">
                        <h4 className="nobilis__block-title">Social</h4>
                        <p className="nobilis__text">
                            Private dinners, salons, and gatherings hosted by members in homes, palaces, châteaux, or other distinctive private venues. Social experiences may also be organized alongside major cultural or sporting events (such as Formula 1, Ascot, Art Basel, the World Cup, opera performances, or similar occasions). These gatherings may be hosted independently and do not require inclusion of access to the main event itself. Intimate formats are favored for meaningful, high-value connections or impact-driven dialogue.
                        </p>

                        <h4 className="nobilis__block-title">Lifestyle & Uniqueness</h4>
                        <p className="nobilis__text">
                            Experiences reflecting how members live around the world and the uniqueness of different lifestyles, communities, and cultures—from sailors, artists, farmers, and explorers to closed, traditional, or tribal communities. These experiences offer authentic, first-hand exposure to diverse ways of living and thinking, often uncommon or inaccessible to the general public.
                        </p>

                        <h4 className="nobilis__block-title">Hobbies & Passions</h4>
                        <p className="nobilis__text">
                            Invitations centered around a member’s personal interests and passions, hosted independently or together with partners. Examples include car rallies, golf trips (including remote or private courses), sailing regattas and races, polo, tennis, arts, craftsmanship, perfumery, wellness retreats, safaris, and other hands-on or experiential pursuits.
                        </p>

                        <h4 className="nobilis__block-title">Cultural & Intellectual</h4>
                        <p className="nobilis__text">
                            Private access to personal collections (art, wine, cars, or other collections), including hosted viewings or introductions. Experiences may also include after-hours museum visits, archaeology-focused journeys, studio, gallery, or atelier visits, and other in-depth engagements with culture, history, and intellectual exploration.
                        </p>

                        <h4 className="nobilis__block-title">Professions & Real-Life Insight</h4>
                        <p className="nobilis__text">
                            Experiences that allow participants to understand what it truly means to live a specific professional life by spending time with the member. This may include artists, athletes, brokers, entrepreneurs, scientists, investors, or professionals across diverse fields. It may help others during the carrier choose/change process or may fulfill curiosity.
                        </p>

                        <h4 className="nobilis__block-title">Impact & Philanthropy</h4>
                        <p className="nobilis__text">
                            Access to initiatives, venues, or projects where impact matters and where resources, time, or attention are meaningfully directed. This may include site visits, presentations, or charity-driven events explaining how impact is created and how support is applied. May help to collect funds for charitable or impact courses.
                        </p>

                        <h4 className="nobilis__block-title">Youth & Next Generation</h4>
                        <p className="nobilis__text">
                            Experiences focused on youth,  the next generation. Members may invite younger participants to learn through real-life exposure, apprenticeships, mentorship, or hands-on experiences in areas such as farming, craftsmanship, business, or creative industries to help to grow better and more educated and responsible humans.
                        </p>

                        <h4 className="nobilis__block-title">Rare & Access-Based</h4>
                        <p className="nobilis__text">
                            Invitations to venues, environments, events, or situations that are typically not publicly accessible, including behind-the-scenes access, private introductions, and events participations, when available through the member.
                        </p>

                        <h4 className="nobilis__block-title">Knowledge, Skill & Mentorship</h4>
                        <p className="nobilis__text">
                            Masterclasses, strategic roundtables, cross-disciplinary exchanges, and mentorship-driven formats led by members sharing deep expertise or perspective.
                        </p>
                    </div>
                </section>
                {/* HOSTING */}
                <section className="nobilis__section">
                    <h2 className="nobilis__label">
                        Experience Hosting & Financial Structure
                    </h2>
                    <p className="nobilis__text">
                        Experiences hosted within Nobilis may be offered under different formats, depending on the intention of the host and the nature of the experience.
                    </p>
                    <div>
                        <div className="nobilis__small-label">
                            Hosting options include:
                        </div>
                        <p className="nobilis__text">
                            <ul className='mb-0'>
                                <li>Commercial experiences, where the host has opportunities for earnings</li>
                                <li>Impact driven experiences, where contributions are collected directly for a designated charitable or impact organization</li>
                                <li>Invitational experiences, offered free of charge at the host’s discretion</li>
                            </ul>
                            In all cases, the hosting member remains fully responsible for the experience content, delivery, and any applicable legal or tax obligations related to payments or donations.
                        </p>
                    </div>
                    <div>
                        <h3 className="nobilis__small-label">
                            Platform participation fee:
                        </h3>
                        <p className="nobilis__text mb-0">
                            A platform participation fee applies to experiences facilitated through Nobilis. For commercial and impact-driven experiences, an 15 - 18.5% platform fee is added to the hosted value. For invitational experiences, a fixed participation fee of $1,500 per guest applies. Applicable taxes and payment processing fees (such as Stripe) are added separately. Fees support platform operations, curation, moderation, and long-term ecosystem development.
                        </p>
                    </div>
                </section>
                {/* submitting an experience */}
                <section className="nobilis__section">
                    <h2 className="nobilis__label">Submitting an Experience</h2>
                    <p className="nobilis__text">
                        Until the Nobilis platform is officially live, experiences are submitted directly to the Nobilis team as part of the onboarding process.
                        Detailed submission requirements, including all information needed to prepare and publish an experience, will be provided separately during onboarding via a dedicated submission form or document.
                        The Nobilis team will review submitted information and may confirm acceptance or suggest refinements prior to publication.
                        Experience Submission & Review Process
                    </p>
                    <div>
                        <div className="nobilis__small-label">
                            Pre-Launch Phase
                        </div>
                        <p className="nobilis__text">
                            During the pre-launch phase, all experiences are:
                            <ul className='mb-0'>
                                <li>Submitted directly to the Nobilis team</li>
                                <li>Reviewed, structured, and prepared for publication</li>
                                <li>Uploaded to the platform on behalf of the member</li>
                            </ul>
                            This ensures consistency, quality, and alignment across the founding network ahead of launch.
                        </p>
                    </div>
                    <div>
                        <div className="nobilis__small-label">
                            Post-Launch Phase
                        </div>
                        <p className="nobilis__text mb-0">
                            Once the Nobilis platform is live:
                            <ul className=''>
                                <li>Members will be able to create, upload, and manage their experiences directly through their Nobilis account</li>
                                <li>Experiences submitted in pre-launch phase will be uploaded to the platform automatically</li>
                            </ul>
                            All experiences are subject to review and may be approved, returned for adjustment, or declined at Nobilis’ discretion.
                        </p>
                    </div>
                </section>
                {/* Media, privacy & confidentiality */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Media, Privacy & Confidentiality</h2>
                        <p className="nobilis__text mb-0">
                            Discretion is foundational to Nobilis.
                            Each experience must clearly define:
                        </p>
                        <p className="nobilis__text">
                            <ul className='mb-0'>
                                <li>Whether photography, video, or recordings are permitted</li>
                                <li>Under what conditions guest consent is required</li>
                                <li>How any content may be used or shared</li>
                            </ul>
                        </p>
                    </div>
                    <div>
                        <p className="nobilis__text">
                            Hosts are responsible for:
                            <ul className=''>
                                <li>Communicating expectations clearly</li>
                                <li>Enforcing agreed media boundaries</li>
                                <li>Respecting guest privacy at all times</li>
                            </ul>
                            If media capture is permitted for an experience, guests must provide clear, affirmative opt-in consent in advance (for example, via pre-event forms or platform check-in).
                            If no such consent is provided, the guest must not be included in any photography, video, or recordings, and any identifiable content must not be used or shared.
                        </p>
                    </div>
                    <div>
                        <p className="nobilis__text mb-0">
                            Hosts are responsible for:
                            <ul className='mb-0'>
                                <li>Ensuring consent requirements are clearly communicated in advance</li>
                                <li>Facilitating the consent process</li>
                                <li>Respecting and enforcing individual guest preferences at all times</li>
                            </ul>
                            Failure to respect agreed confidentiality or media boundaries may result in temporary or permanent suspension of hosting privileges or other appropriate platform actions, in line with Nobilis community standards.
                        </p>
                    </div>
                </section>
                {/* Cancelation policy */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Cancellation Policy</h2>
                        <p className="nobilis__text">
                            Cancellations impact trust, reliability, and the integrity of the Nobilis community. For this reason, cancellations are handled with care and transparency.
                        </p>
                        <div className="nobilis__small-label">
                            Host Cancellations
                        </div>
                        <p className="nobilis__text">
                            <ul className='mb-0'>
                                <li>Host cancellations should occur only in exceptional circumstances, such as force majeure events, serious illness, or death.</li>
                                <li>Hosts must notify both guests and the Nobilis team as early as possible, clearly explaining the reason for the cancellation.</li>
                                <li>Where feasible, hosts should indicate whether the experience may be rescheduled or if it must be cancelled entirely.</li>
                                <li>Any applicable refunds must follow the terms stated for the specific experience.</li>
                                <li>Repeated or last-minute cancellations may affect future hosting eligibility or result in temporary or permanent restrictions, in line with Nobilis community standards.</li>
                            </ul>
                        </p>
                        <div className="nobilis__small-label">
                            Guest Cancellations
                        </div>
                        <p className="nobilis__text">
                            <ul className='mb-0'>
                                <li>Guest cancellations are governed by the cancellation terms defined within each individual experience.</li>
                                <li>Refund eligibility depends on the timing of the cancellation and the structure of the experience.</li>
                            </ul>
                        </p>
                        <div className="nobilis__small-label">
                            Platform Role
                        </div>
                        <p className="nobilis__text mb-0">
                            Nobilis acts as a platform facilitator and reserves the right to review cancellation cases and intervene when necessary to ensure fairness, consistency, and community integrity.
                        </p>
                    </div>
                </section>
                {/* Curated “Ready-to-Go” Experiences */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Curated “Ready-to-Go” Experiences</h2>
                        <p className="nobilis__text mb-0">
                            Nobilis develops a selection of curated, ready-to-host experience formats in collaboration with trusted partners. These may include access to unique venues (such as golf courses, private or after-hours museum access, and other unique venues) as well as extraordinary travel experiences (including expeditions, safaris, retreats, or similar formats).<br />
                            These curated formats are selectively offered to Members who are aligned with the nature of the experience and are able to lead, host, or meaningfully represent the experience within the Nobilis ecosystem. Eligible Members may receive preferential terms or credits, as defined on a case-by-case basis.<br />
                            Members who are not initially offered a curated format may propose new experience concepts. Where aligned with Nobilis standards and partner availability, such experiences may be developed collaboratively, with the proposing Member given the opportunity to host or lead the experience.<br />
                            Curated “Ready-to-Go” experiences are subject to availability, partner constraints, and Nobilis approval, and may evolve over time.
                        </p>
                    </div>
                </section>
                {/* Review & Approval */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Review & Approval</h2>
                        <p className="nobilis__text mb-0">
                            All experiences are reviewed prior to publication.<br />
                            Nobilis reserves the right to:
                        </p>
                        
                        <p className="nobilis__text mb-0">
                            <ul className='mb-0'>
                                <li>Request clarification or refinement.</li>
                                <li>Decline experiences that do not meet standards.</li>
                                <li>Suspend or remove listings if concerns arise.</li>
                            </ul>
                            This process exists to protect members, hosts, and the ecosystem as a whole.
                        </p>
                    </div>
                </section>
                {/* Independent Hosting & Responsibility */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Independent Hosting & Responsibility</h2>
                        <p className="nobilis__text mb-0">
                            Experiences hosted through Nobilis are independently created and led by members.<br />
                            Nobilis:
                        </p>
                        <p className="nobilis__text mb-0">
                            <ul className='mb-0'>
                                <li>Is not the organizer or operator of experiences.</li>
                                <li>Does not guarantee outcomes, safety, or delivery.</li>
                                <li>Facilitates connection and participation only.</li>
                            </ul>
                        </p>
                        <p className="nobilis__text mb-0">
                            Hosts are solely responsible for:
                        </p>
                        <p className="nobilis__text mb-0">
                            <ul className='mb-0'>
                                <li>Experience delivery.</li>
                                <li>Guest safety and well-being.</li>
                                <li>Compliance with all applicable local laws, regulations, and tax obligations.</li>
                            </ul>
                        </p>
                    </div>
                </section>
                {/* Liability & Assumption of Risk */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Liability & Assumption of Risk</h2>
                        <p className="nobilis__text mb-0">
                            Participation in experiences may involve travel, physical activity, or environments beyond Nobilis’ control.<br />
                            By hosting or participating, members acknowledge that:
                        </p>
                        <p className="nobilis__text mb-0">
                            <ul className='mb-0'>
                                <li>Participation is voluntary.</li>
                                <li>Certain risks may be inherent.</li>
                                <li>Nobilis is not liable for injury, loss, damage, or expenses arising from participation.</li>
                            </ul>
                            Nothing in these guidelines limits liability where such limitation is prohibited by law.
                        </p>
                    </div>
                </section>
                {/* Intellectual Property & Content */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Intellectual Property & Content</h2>
                        <p className="nobilis__text mb-0">
                            By submitting an experience, hosts grant Nobilis a non-exclusive, royalty-free right to:
                        </p>
                        <p className="nobilis__text mb-0">
                            <ul className='mb-0'>
                                <li>Display experience titles, descriptions, and approved imagery within the platform.</li>
                                <li>Use such materials for platform-related communication and curation.</li>
                            </ul>
                            Hosts confirm that submitted content does not infringe third-party rights.<br />
                            Ownership of the experience itself remains with the host.
                        </p>
                    </div>
                </section>
                {/* Suspension, Removal & Access */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Suspension, Removal & Access</h2>
                        <p className="nobilis__text mb-0">
                            To protect the community, Nobilis reserves the right to:
                        </p>
                        <p className="nobilis__text mb-0">
                            <ul className='mb-0'>
                                <li>Decline, suspend, or remove experiences.</li>
                                <li>Limit hosting or participation privileges.</li>
                                <li>Terminate access to the platform.</li>
                            </ul>
                            Such actions may be taken at Nobilis’ discretion and without obligation to disclose detailed reasoning.
                        </p>
                    </div>
                </section>
                {/* Force Majeure */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Force Majeure</h2>
                        <p className="nobilis__text mb-0">
                            Nobilis is not responsible for delays or disruptions caused by events beyond reasonable control, including natural events, travel restrictions, government actions, or health and safety emergencies.
                        </p>
                    </div>
                </section>
                {/* Governing Law */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Governing Law</h2>
                        <p className="nobilis__text mb-0">
                            These guidelines are governed by the laws of the jurisdiction of Texas, USA
                        </p>
                    </div>
                </section>
                {/* Final Provision */}
                <section className="nobilis__section">
                    <div>
                        <h2 className="nobilis__label mb-0">Final Provision</h2>
                        <p className="nobilis__text mb-0">
                            These guidelines form part of the broader Nobilis participation framework.<br />
                            Nobilis reserves the right to evolve platform features, experience structures, and policies in alignment with the community’s long-term integrity.
                        </p>
                    </div>
                </section>
                <section className="nobilis__section">
                    <p className="nobilis__text mb-0">
                        Nobilis Global LLC<br />
                        10900 Research Blvd<br />
                        Ste 160C #1119<br />
                        Austin, TX 78759<br />
                        United States
                    </p>
                </section>
            </div>
        </main>
    )
}

export { GuidePage }
